import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { useUser } from '../context/UserContext';
import { useWallet } from '../context/WalletContext';
import Navbar from './Navbar';
import { 
  Clock, 
  Users, 
  Target,
  Timer,
  Coins,
  Award,
  TrendingUp,
  CheckCircle,
  Trophy,
  Star,
  ArrowLeft,
  Play
} from 'lucide-react';

const ContestDetail = () => {
  const { contestId } = useParams();
  const navigate = useNavigate();
  const { user } = useUser();
  const { wallet } = useWallet();
  const [contest, setContest] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchContestDetails();
  }, [contestId]);

  const fetchContestDetails = async () => {
    // Demo contest data based on contestId
    const demoContests = {
      "live_1": {
        id: "live_1",
        title: "Sports Quiz Championship",
        description: "Test your sports knowledge and win big in this amazing contest with real estate prizes.",
        status: "live",
        entry_fee: 0,
        total_questions: 15,
        duration: 600, // 10 minutes
        enrolled_count: 324,
        max_participants: 500,
        prize_pool: "₹8,00,000",
        first_prize: "Residential Land Worth ₹5,00,000",
        category: "Sports",
        difficulty: "Medium",
        rules: {
          duration: "10 minutes",
          questions: "15 questions",
          scoring: "+20 points for correct, -4 for wrong",
          time_bonus: "Remaining seconds as bonus points",
          streak_bonus: "3 correct: +10, 5 correct: +20, 10 correct: +50"
        },
        prizes: {
          "1st": "Residential Land Worth ₹5,00,000",
          "2nd": "iPhone 15 Pro",
          "3rd": "Apple Watch Ultra",
          "4th-10th": "Cash Prize ₹10,000",
          "11th-25th": "Cash Prize ₹5,000",
          "26th-50th": "Cash Prize ₹2,000",
          "51st-100th": "Gift Vouchers",
          "101st-200th": "Entry Fee Refund"
        }
      },
      "live_2": {
        id: "live_2", 
        title: "Entertainment Quiz Challenge",
        description: "Movies, music, and celebrity trivia! Ultimate entertainment contest with amazing prizes.",
        status: "live",
        entry_fee: 0,
        total_questions: 15,
        duration: 600,
        enrolled_count: 567,
        max_participants: 1000,
        prize_pool: "₹6,50,000",
        first_prize: "Agricultural Land Worth ₹3,00,000",
        category: "Entertainment",
        difficulty: "Medium",
        rules: {
          duration: "10 minutes",
          questions: "15 questions", 
          scoring: "+20 points for correct, -4 for wrong",
          time_bonus: "Remaining seconds as bonus points",
          streak_bonus: "3 correct: +10, 5 correct: +20, 10 correct: +50"
        },
        prizes: {
          "1st": "Agricultural Land Worth ₹3,00,000",
          "2nd": "MacBook Air M2", 
          "3rd": "Samsung Galaxy Watch",
          "4th-10th": "Cash Prize ₹8,000",
          "11th-25th": "Cash Prize ₹4,000",
          "26th-50th": "Cash Prize ₹1,500",
          "51st-100th": "Gift Vouchers",
          "101st-200th": "Entry Fee Refund"
        }
      },
      "live_3": {
        id: "live_3",
        title: "General Knowledge Quiz",
        description: "Ultimate knowledge challenge for everyone! Test your general knowledge and win amazing prizes.",
        status: "live", 
        entry_fee: 0,
        total_questions: 15,
        duration: 600,
        enrolled_count: 445,
        max_participants: 750,
        prize_pool: "₹7,50,000",
        first_prize: "Commercial Land Worth ₹4,00,000",
        category: "General Knowledge",
        difficulty: "Hard",
        rules: {
          duration: "10 minutes",
          questions: "15 questions",
          scoring: "+20 points for correct, -4 for wrong", 
          time_bonus: "Remaining seconds as bonus points",
          streak_bonus: "3 correct: +10, 5 correct: +20, 10 correct: +50"
        },
        prizes: {
          "1st": "Commercial Land Worth ₹4,00,000",
          "2nd": "iPhone 15 Pro Max",
          "3rd": "Apple Watch Series 9", 
          "4th-10th": "Cash Prize ₹12,000",
          "11th-25th": "Cash Prize ₹6,000",
          "26th-50th": "Cash Prize ₹2,500",
          "51st-100th": "Gift Vouchers",
          "101st-200th": "Entry Fee Refund"
        }
      }
    };

    // Set demo data immediately
    const demoContest = demoContests[contestId] || demoContests["live_1"];
    setContest(demoContest);
    setLoading(false);

    // Try to fetch real data in background (for future updates)
    try {
      const response = await fetch(`/api/contests/${contestId}`);
      if (response.ok) {
        const data = await response.json();
        if (data && data.id) {
          setContest(data);
        }
      }
    } catch (error) {
      console.log('Backend not available, using demo data:', error);
      // Demo data already set, no need to handle error
    }
  };

  const handleStartContest = async () => {
    if (!wallet || wallet.total_amount < contest.entry_fee) {
      alert('Insufficient wallet balance. Please add money to your wallet.');
      navigate('/wallet');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      
      const response = await fetch(`/api/quiz/session/start?contest_id=${contestId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      
      if (response.ok) {
        // Navigate to quiz page with session ID
        navigate(`/quiz/${data.session_id}`);
      } else {
        alert(data.detail || 'Failed to start contest');
      }
    } catch (error) {
      console.log('Backend not available, using demo quiz:', error);
      // For demo mode, create a mock session ID and navigate to quiz
      const demoSessionId = `demo_session_${Date.now()}`;
      navigate(`/quiz/${demoSessionId}`);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading contest details...</p>
        </div>
      </div>
    );
  }

  if (!contest) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600">Contest not found</p>
          <Button onClick={() => navigate('/dashboard')} className="mt-4">
            Back to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Contest Header - Improved Structure */}
        <Card className="border border-gray-200 bg-white shadow-lg">
          <CardHeader className="pb-4 px-4 pt-4">
            {/* Top Section */}
            <div className="flex flex-col md:flex-row items-start justify-between space-y-4 md:space-y-0 mb-4">
              <div className="flex-1 w-full md:w-auto md:pr-6">
                <div className="flex items-center space-x-3 mb-3">
                  <Target className="h-6 w-6 text-red-600" />
                </div>
                <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3 leading-tight">{contest.title}</h1>
                <p className="text-gray-600 leading-relaxed text-base md:text-lg">{contest.description}</p>
              </div>
              <div className="text-center md:text-right w-full md:w-auto md:min-w-[200px]">
                <div className="bg-white rounded-lg p-4 shadow-sm border">
                  <div className="text-sm text-gray-500 mb-1">Total Prize Pool</div>
                  <div className="text-3xl md:text-4xl font-bold text-red-600">{contest.prize_pool}</div>
                </div>
              </div>
            </div>
            
            {/* Prize Highlight Section */}
            <div className="bg-gray-50 rounded-lg p-4 border">
              <div className="flex items-center space-x-4">
                <div className="bg-red-100 p-3 rounded-full">
                  <Award className="h-6 w-6 text-red-600" />
                </div>
                <div>
                  <div className="text-sm text-gray-600 mb-1">🏆 First Prize</div>
                  <div className="text-xl font-bold text-gray-900">{contest.first_prize}</div>
                </div>
              </div>
            </div>
            
            {/* Mobile Action Button */}
            <div className="mt-4 md:hidden">
              <Button 
                onClick={handleStartContest}
                className="w-full bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white py-4 text-lg font-semibold rounded-xl shadow-lg"
              >
                <Play className="h-5 w-5 mr-3" />
                Join Contest & Start Quiz
              </Button>
            </div>
          </CardHeader>
        </Card>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            
            {/* Contest Stats - Improved Layout */}
            <Card className="shadow-sm">
              <CardHeader className="pb-4">
                <CardTitle className="text-xl flex items-center">
                  <Users className="h-5 w-5 mr-2 text-red-600" />
                  Contest Overview
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Stats Grid */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="text-center p-4 bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl border border-blue-200">
                    <Users className="h-8 w-8 text-blue-600 mx-auto mb-3" />
                    <div className="text-2xl font-bold text-blue-900">{contest.enrolled_count}</div>
                    <div className="text-sm text-blue-700 font-medium">Already Participated</div>
                  </div>
                  <div className="text-center p-4 bg-gradient-to-br from-green-50 to-green-100 rounded-xl border border-green-200">
                    <Trophy className="h-8 w-8 text-green-600 mx-auto mb-3" />
                    <div className="text-2xl font-bold text-green-900">200</div>
                    <div className="text-sm text-green-700 font-medium">Total Winners</div>
                  </div>
                  <div className="text-center p-4 bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl border border-purple-200">
                    <Clock className="h-8 w-8 text-purple-600 mx-auto mb-3" />
                    <div className="text-2xl font-bold text-purple-900">10</div>
                    <div className="text-sm text-purple-700 font-medium">Minutes Duration</div>
                  </div>
                </div>

                {/* Contest Info */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center space-x-2">
                      <Target className="h-4 w-4 text-gray-600" />
                      <span className="text-gray-700">Contest Category:</span>
                    </div>
                    <span className="font-medium text-gray-900">{contest.category}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm mt-2">
                    <div className="flex items-center space-x-2">
                      <Star className="h-4 w-4 text-gray-600" />
                      <span className="text-gray-700">Difficulty Level:</span>
                    </div>
                    <span className="font-medium text-gray-900">{contest.difficulty}</span>
                  </div>
                </div>
                
                {/* Start Contest Button - Web only */}
                <div className="hidden md:block">
                  <Button 
                    onClick={handleStartContest}
                    className="w-full bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white py-4 text-lg font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-200"
                  >
                    <Play className="h-5 w-5 mr-3" />
                    Join Contest & Start Quiz
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Quiz Rules - Improved Structure */}
            <Card className="shadow-sm">
              <CardHeader className="pb-4">
                <CardTitle className="text-xl flex items-center">
                  <CheckCircle className="h-5 w-5 mr-2 text-green-600" />
                  Contest Rules & Scoring
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Basic Rules */}
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="flex items-center space-x-4 p-4 bg-gradient-to-r from-blue-50 to-blue-100 rounded-xl border border-blue-200">
                    <div className="bg-blue-600 p-2 rounded-lg">
                      <Clock className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <div className="font-bold text-blue-900">Contest Duration</div>
                      <div className="text-blue-700">10 minutes total</div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4 p-4 bg-gradient-to-r from-orange-50 to-orange-100 rounded-xl border border-orange-200">
                    <div className="bg-orange-600 p-2 rounded-lg">
                      <Timer className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <div className="font-bold text-orange-900">Per Question</div>
                      <div className="text-orange-700">15 seconds each</div>
                    </div>
                  </div>
                </div>
                
                {/* Scoring System */}
                <div>
                  <h4 className="font-bold text-lg mb-4 flex items-center">
                    <Coins className="h-5 w-5 mr-2 text-yellow-600" />
                    Scoring System
                  </h4>
                  <div className="grid md:grid-cols-3 gap-3">
                    <div className="p-4 bg-gradient-to-br from-green-50 to-green-100 rounded-xl border border-green-200 text-center">
                      <div className="text-2xl mb-2">✅</div>
                      <div className="font-bold text-green-900">Right Answer</div>
                      <div className="text-xl font-bold text-green-600">+20 points</div>
                    </div>
                    <div className="p-4 bg-gradient-to-br from-red-50 to-red-100 rounded-xl border border-red-200 text-center">
                      <div className="text-2xl mb-2">❌</div>
                      <div className="font-bold text-red-900">Wrong Answer</div>
                      <div className="text-xl font-bold text-red-600">-4 points</div>
                    </div>
                    <div className="p-4 bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl border border-blue-200 text-center">
                      <div className="text-2xl mb-2">⚡</div>
                      <div className="font-bold text-blue-900">Time Bonus</div>
                      <div className="text-sm font-bold text-blue-600">Remaining seconds</div>
                    </div>
                  </div>
                </div>

                {/* Streak Bonuses */}
                <div>
                  <h4 className="font-bold text-lg mb-4 flex items-center">
                    <TrendingUp className="h-5 w-5 mr-2 text-purple-600" />
                    Consecutive Correct Bonuses
                  </h4>
                  <div className="grid grid-cols-3 gap-3">
                    <div className="text-center p-4 bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-xl border border-yellow-200">
                      <div className="text-2xl font-bold text-yellow-900 mb-1">3</div>
                      <div className="text-sm text-yellow-700 mb-1">Correct Answers</div>
                      <div className="font-bold text-yellow-600">+10 pts</div>
                    </div>
                    <div className="text-center p-4 bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl border border-orange-200">
                      <div className="text-2xl font-bold text-orange-900 mb-1">5</div>
                      <div className="text-sm text-orange-700 mb-1">Correct Answers</div>
                      <div className="font-bold text-orange-600">+20 pts</div>
                    </div>
                    <div className="text-center p-4 bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl border border-purple-200">
                      <div className="text-2xl font-bold text-purple-900 mb-1">10</div>
                      <div className="text-sm text-purple-700 mb-1">Correct Answers</div>
                      <div className="font-bold text-purple-600">+50 pts</div>
                    </div>
                  </div>
                </div>

              </CardContent>
            </Card>

          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            
            {/* Prize Distribution */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center">
                  <Trophy className="h-5 w-5 mr-2 text-orange-500" />
                  Prize Breakup
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="space-y-4">
                  {Object.entries(contest.prizes).map(([position, prize], index) => {
                    let bgColor = "bg-gray-50";
                    let textColor = "text-gray-900";
                    let rankColor = "bg-gray-500";
                    let prizeIcon = "🎁";
                    
                    if (index === 0) {
                      bgColor = "bg-yellow-50";
                      textColor = "text-yellow-900";
                      rankColor = "bg-yellow-500";
                      prizeIcon = "🏆";
                    } else if (index === 1) {
                      bgColor = "bg-gray-100";
                      textColor = "text-gray-900";
                      rankColor = "bg-gray-500";
                      prizeIcon = prize.includes("iPhone") || prize.includes("MacBook") ? "📱" : prize.includes("Land") || prize.includes("Plot") ? "🏘️" : "🥈";
                    } else if (index === 2) {
                      bgColor = "bg-orange-50";
                      textColor = "text-orange-900";
                      rankColor = "bg-orange-500";
                      prizeIcon = prize.includes("Watch") ? "⌚" : prize.includes("MacBook") ? "💻" : "🥉";
                    } else if (prize.includes("Cash")) {
                      prizeIcon = "💰";
                    } else if (prize.includes("Voucher")) {
                      prizeIcon = "🎫";
                    } else if (prize.includes("Refund")) {
                      prizeIcon = "💸";
                    }
                    
                    return (
                      <div key={position} className={`p-4 ${bgColor} rounded-lg border border-gray-200 transition-all duration-200 hover:shadow-md hover:scale-[1.02] cursor-pointer`}>
                        <div className="flex items-start justify-between">
                          <div className="flex items-center space-x-3 min-w-0 flex-shrink-0">
                            <div className={`w-8 h-8 ${rankColor} rounded-full flex items-center justify-center`}>
                              <span className="text-white text-xs font-bold">
                                {position === '1st' ? '1' : 
                                 position === '2nd' ? '2' : 
                                 position === '3rd' ? '3' : 
                                 position.split('-')[0]}
                              </span>
                            </div>
                            <span className="text-sm font-medium text-gray-700">{position}</span>
                            <span className="text-lg">{prizeIcon}</span>
                          </div>
                          <div className="text-right ml-3 flex-1">
                            <span className={`font-bold ${textColor} text-sm leading-relaxed break-words`}>{prize}</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* Question Categories */}
            <Card className="shadow-sm">
              <CardHeader className="pb-4">
                <CardTitle className="text-lg flex items-center">
                  <Target className="h-5 w-5 mr-2 text-indigo-600" />
                  Contest Categories
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="space-y-4">
                  <div className="flex items-center space-x-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <div className="bg-blue-600 p-2 rounded-full">
                      <Trophy className="h-4 w-4 text-white" />
                    </div>
                    <span className="font-medium text-blue-900">Sports</span>
                  </div>
                  <div className="flex items-center space-x-3 p-3 bg-purple-50 rounded-lg border border-purple-200">
                    <div className="bg-purple-600 p-2 rounded-full">
                      <Star className="h-4 w-4 text-white" />
                    </div>
                    <span className="font-medium text-purple-900">Entertainment</span>
                  </div>
                  <div className="flex items-center space-x-3 p-3 bg-green-50 rounded-lg border border-green-200">
                    <div className="bg-green-600 p-2 rounded-full">
                      <CheckCircle className="h-4 w-4 text-white" />
                    </div>
                    <span className="font-medium text-green-900">General Knowledge</span>
                  </div>
                  <div className="flex items-center space-x-3 p-3 bg-orange-50 rounded-lg border border-orange-200">
                    <div className="bg-orange-600 p-2 rounded-full">
                      <TrendingUp className="h-4 w-4 text-white" />
                    </div>
                    <span className="font-medium text-orange-900">Current Affairs</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Start Contest Button */}
            <Card className="bg-gradient-to-br from-red-600 to-red-700 text-white shadow-xl">
              <CardContent className="p-6 text-center">
                <div className="bg-white/20 p-3 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                  <Trophy className="h-8 w-8 text-white" />
                </div>
                <h3 className="font-bold text-xl mb-2">Ready to Participate?</h3>
                <p className="text-red-100 text-sm mb-6 leading-relaxed">
                  Test your knowledge across multiple categories and win amazing prizes including land and premium gadgets!
                </p>
                <div className="mb-6 p-4 bg-white/10 rounded-xl border border-white/20">
                  <div className="flex items-center justify-center space-x-2 mb-2">
                    <Award className="h-5 w-5 text-yellow-300" />
                    <div className="text-sm text-red-100 font-medium">First Prize Winner Gets</div>
                  </div>
                  <div className="text-lg font-bold text-white">{contest.first_prize}</div>
                </div>
                <Button 
                  onClick={handleStartContest}
                  className="w-full bg-white text-red-600 hover:bg-gray-100 font-bold py-4 text-lg rounded-xl shadow-lg hover:shadow-xl transition-all duration-200"
                >
                  <Play className="h-5 w-5 mr-3" />
                  Join Contest Now
                </Button>
              </CardContent>
            </Card>

          </div>
        </div>
      </div>
    </div>
  );
};

export default ContestDetail;